import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  finalizeAuthenticatedUser,
  login as loginService,
  TwoFactorChallenge,
} from '../../../services/authService';
import {
  completeAuthentication,
  completeRegistration,
  isSupported as webAuthnSupported,
  recoveryLogin,
} from '../../../services/webauthnService';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import RecoveryCodesDisplay from '../components/RecoveryCodesDisplay';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { resolveNextStepUrl } from '../../../routing/nextStep';

const Login = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null);
  const [pendingUser, setPendingUser] = useState<{ nextStep?: string } | null>(null);

  const successMessage = location.state?.message;

  const redirectAfterLogin = (user: { nextStep?: string }) => {
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirect');
    const safePath = redirectTo?.startsWith('/') ? redirectTo : resolveNextStepUrl(user.nextStep as never);
    navigate(safePath);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const outcome = await loginService(email, password);
      if (outcome.kind === 'authenticated') {
        auth.login(outcome.user);
        redirectAfterLogin(outcome.user);
        return;
      }
      if (!webAuthnSupported()) {
        setError("Ce navigateur ne supporte pas l'authentification biométrique requise.");
        return;
      }
      setChallenge(outcome.challenge);
    } catch (err) {
      console.error('Erreur de connexion', err);
      setError(err instanceof Error ? err.message : 'Identifiants incorrects.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBiometric = async () => {
    if (!challenge) return;
    setError('');
    setIsLoading(true);
    try {
      if (challenge.mode === 'verify') {
        const data = await completeAuthentication(
          challenge.intentId,
          challenge.options as PublicKeyCredentialRequestOptionsJSON
        );
        const { user } = finalizeAuthenticatedUser(data, email);
        auth.login(user);
        redirectAfterLogin(user);
        return;
      }

      const data = await completeRegistration(
        challenge.intentId,
        challenge.options as PublicKeyCredentialCreationOptionsJSON,
        navigator.userAgent.slice(0, 80)
      );
      const { user } = finalizeAuthenticatedUser(data, email);
      auth.login(user);

      if (data.recoveryCodes && data.recoveryCodes.length > 0) {
        setGeneratedCodes(data.recoveryCodes);
        setPendingUser(user);
        return;
      }
      redirectAfterLogin(user);
    } catch (err) {
      console.error('Erreur 2FA', err);
      setError(err instanceof Error ? err.message : 'Vérification biométrique échouée.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverySubmit = async () => {
    if (!challenge || !recoveryCode.trim()) return;
    setError('');
    setIsLoading(true);
    try {
      const data = await recoveryLogin(challenge.intentId, recoveryCode.trim());
      const { user } = finalizeAuthenticatedUser(data, email);
      auth.login(user);
      redirectAfterLogin(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide.');
    } finally {
      setIsLoading(false);
    }
  };

  const dismissGeneratedCodes = () => {
    const user = pendingUser ?? { nextStep: undefined };
    setGeneratedCodes(null);
    setPendingUser(null);
    redirectAfterLogin(user);
  };

  if (generatedCodes) {
    return <RecoveryCodesDisplay codes={generatedCodes} onClose={dismissGeneratedCodes} />;
  }

  if (challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mcdm-primary-200 dark:bg-mcdm-third-500">
        <div className="bg-white dark:bg-mcdm-third-400 shadow-sm rounded-lg px-8 pt-6 pb-8 max-w-md text-center">
          <h2 className="text-xl mb-4 font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
            {challenge.mode === 'setup' ? 'Activer la 2FA' : 'Vérification biométrique'}
          </h2>
          {!showRecovery ? (
            <>
              <p className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 mb-6">
                {challenge.mode === 'setup'
                  ? 'Pour finaliser la connexion, enregistrez Touch ID, Face ID ou Windows Hello.'
                  : 'Validez votre identité avec la méthode biométrique de votre appareil.'}
              </p>
              {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
              <button
                type="button"
                onClick={handleBiometric}
                disabled={isLoading}
                className="w-full bg-mcdm-green-400 hover:bg-mcdm-green-500 disabled:opacity-50 text-white py-2 px-4 rounded mb-3"
              >
                {isLoading ? 'Vérification…' : challenge.mode === 'setup' ? "Enregistrer l'appareil" : 'Valider'}
              </button>
              {challenge.mode === 'verify' && (
                <button
                  type="button"
                  onClick={() => setShowRecovery(true)}
                  className="block mx-auto text-xs text-mcdm-green-500 hover:text-mcdm-green-600 underline mb-2"
                >
                  Pas accès à votre appareil ? Utiliser un code de récupération
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200 mb-4">
                Saisissez l'un de vos codes de récupération à usage unique.
              </p>
              {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                placeholder="XXXX-XXXXXX"
                className="w-full px-3 py-2 border rounded mb-3 font-mono text-center tracking-wider dark:bg-mcdm-primary-100"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={handleRecoverySubmit}
                disabled={isLoading || !recoveryCode.trim()}
                className="w-full bg-mcdm-green-400 hover:bg-mcdm-green-500 disabled:opacity-50 text-white py-2 px-4 rounded mb-3"
              >
                {isLoading ? 'Vérification…' : 'Valider le code'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRecovery(false);
                  setRecoveryCode('');
                  setError('');
                }}
                className="block mx-auto text-xs text-mcdm-secondary-300 hover:text-mcdm-secondary-400 mb-2"
              >
                Retour à la biométrie
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setChallenge(null);
              setShowRecovery(false);
              setRecoveryCode('');
              setError('');
            }}
            className="text-xs text-mcdm-secondary-300 hover:text-mcdm-secondary-400"
          >
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-mcdm-primary-200 dark:bg-mcdm-third-500">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-mcdm-third-400 shadow-sm shadow-inner rounded-lg px-8 pt-6 pb-8 mb-4">
        <h2 className="text-xl mb-6 text-center font-400 font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
          Connectez-vous
        </h2>

        {successMessage && (
          <div className="mb-4 p-3 bg-mcdm-secondary-100 dark:bg-mcdm-secondary-300 border border-mcdm-secondary-200 dark:border-mcdm-secondary-400 text-mcdm-secondary-400 dark:text-white rounded text-sm">
            {successMessage}
          </div>
        )}

        <div className="mb-4">
          <label className="text-mcdm-third-500 dark:text-mcdm-primary-200 text-sm" htmlFor="email">
            Email
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-mcdm-third-500 dark:text-mcdm-third-100 dark:bg-mcdm-primary-100 leading-tight focus:outline-none focus:shadow-outline"
            id="email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="mb-4">
          <label className="text-mcdm-third-500 dark:text-mcdm-primary-200 text-sm" htmlFor="password">
            Mot de passe
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-2 px-3 text-mcdm-third-500 dark:text-mcdm-third-100 dark:bg-mcdm-primary-100 mb-3 leading-tight focus:outline-none focus:shadow-outline"
            id="password"
            type="password"
            placeholder="******************"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error && <p className="text-red-500 text-xs italic mb-4 text-center">{error}</p>}

        <div className="flex flex-col items-center">
          <button
            className={`w-full bg-mcdm-green-400 dark:bg-mcdm-green-400 text-white dark:text-mcdm-primary-100 py-2 px-4 rounded shadow-inner focus:outline-none focus:shadow-outline mb-4 ${isLoading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-mcdm-green-500 dark:hover:bg-mcdm-green-500'
              }`}
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connexion...
              </>
            ) : (
              'Connexion'
            )}
          </button>

          <div className="flex flex-col items-center space-y-2 w-full">
            <Link
              to="/mot-de-passe-oublie"
              className="text-sm text-mcdm-secondary-300 dark:text-mcdm-primary-100 hover:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-200"
            >
              Mot de passe oublié ?
            </Link>
            <Link
              to="/register"
              className="text-sm text-mcdm-secondary-300 dark:text-mcdm-primary-100 hover:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-200"
            >
              Pas de compte ? S'inscrire
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Login;